import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import '../styles/PasswordChange.css';

// Import updatePassword instead of changePassword based on v6 docs
import { 
  updatePassword,
  getCurrentUser
} from 'aws-amplify/auth';

// Debug control - use i.e. DEBUG && console.log("whatever") and set below const true
const DEBUG = process.env.REACT_APP_DEBUG == 'enabled';

const PasswordChange = () => {
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const validatePassword = (password) => {
    // Password must be at least 12 characters long and contain at least:
    // 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character, can't be last 6 passwords
    const minLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
    
    return minLength && hasUppercase && hasLowercase && hasNumber && hasSpecial;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.oldPassword || !formData.newPassword || !formData.confirmPassword) {
      toast.error('All fields are required');
      return;
    }
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (!validatePassword(formData.newPassword)) {
      toast.error('Password must be at least 8 characters long and contain uppercase, lowercase, number, and special character');
      return;
    }
    
    setLoading(true);
    
    try {
      // Change password with v6 style using updatePassword
      await updatePassword({
        oldPassword: formData.oldPassword,
        newPassword: formData.newPassword
      });
      
      // Reset form
      setFormData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      toast.success('Password changed successfully');
    } catch (error) {
        
      if (error.name === 'NotAuthorizedException') {
        toast.error('Incorrect current password');
      } else if (error.name === 'PasswordHistoryPolicyViolationException') {
        toast.error('You cannot repeat any of your last 6 passwords');
      } else {
        toast.error(error.message || 'Failed to change password');
        console.error('Error changing password:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    switch (field) {
      case 'old':
        setShowOldPassword(!showOldPassword);
        break;
      case 'new':
        setShowNewPassword(!showNewPassword);
        break;
      case 'confirm':
        setShowConfirmPassword(!showConfirmPassword);
        break;
      default:
        break;
    }
  };

  return (
    <div className="password-change">
      <h2>Change Password</h2>
      
      <form onSubmit={handleSubmit} className="password-form">
        <div className="form-field">
          <label htmlFor="oldPassword">Current Password</label>
          <div className="password-input-container">
            <input
              id="oldPassword"
              name="oldPassword"
              type={showOldPassword ? 'text' : 'password'}
              value={formData.oldPassword}
              onChange={handleChange}
              placeholder="Enter current password"
              required
            />
            <button 
              type="button" 
              className="toggle-visibility"
              onClick={() => togglePasswordVisibility('old')}
              aria-label={showOldPassword ? 'Hide password' : 'Show password'}
            >
              {showOldPassword ? <FiEyeOff /> : <FiEye />}
            </button>
          </div>
        </div>
        
        <div className="form-field">
          <label htmlFor="newPassword">New Password</label>
          <div className="password-input-container">
            <input
              id="newPassword"
              name="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              value={formData.newPassword}
              onChange={handleChange}
              placeholder="Enter new password"
              required
            />
            <button 
              type="button" 
              className="toggle-visibility"
              onClick={() => togglePasswordVisibility('new')}
              aria-label={showNewPassword ? 'Hide password' : 'Show password'}
            >
              {showNewPassword ? <FiEyeOff /> : <FiEye />}
            </button>
          </div>
          <div className="password-requirements">
            <p>Password must:</p>
            <ul>
              <li className={formData.newPassword.length >= 12 ? 'valid' : ''}>
                Be at least 12 characters
              </li>
              <li className={/[A-Z]/.test(formData.newPassword) ? 'valid' : ''}>
                Include uppercase letter
              </li>
              <li className={/[a-z]/.test(formData.newPassword) ? 'valid' : ''}>
                Include lowercase letter
              </li>
              <li className={/[0-9]/.test(formData.newPassword) ? 'valid' : ''}>
                Include a number
              </li>
              <li className={/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.newPassword) ? 'valid' : ''}>
                Include a special character
              </li>
              <li>
                <strong>You cannot repeat any of your last 6 passwords</strong>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="form-field">
          <label htmlFor="confirmPassword">Confirm New Password</label>
          <div className="password-input-container">
            <input
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm new password"
              required
            />
            <button 
              type="button" 
              className="toggle-visibility"
              onClick={() => togglePasswordVisibility('confirm')}
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
            </button>
          </div>
          {formData.newPassword && formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
            <div className="password-mismatch">
              Passwords do not match
            </div>
          )}
        </div>
        
        <button 
          type="submit" 
          className="change-password-button"
          disabled={loading || !formData.oldPassword || !formData.newPassword || !formData.confirmPassword}
        >
          {loading ? 'Changing Password...' : 'Change Password'}
        </button>
      </form>
    </div>
  );
};

export default PasswordChange;